import React from "react";
import { Text, Link, Flex, LinearProgressIndicator, Tag } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { type TicketStatus } from "#graphql/supergraphTypes";
import { formatTicketStatus } from "../utils";
import * as styles from "./styles_vanilla.css";
import { TypedAGGridCellRenderParams, TicketRowItem } from "./types";

export const WorkTypeRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  if (!data?.workType) return null;

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral700}>
        {data.workType}
      </Text>
    </Flex>
  );
};

export const JiraLinkRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  if (!data?.jiraLink) return null;

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Link
        href={data.jiraLink}
        shouldOpenInNewTab
        icon="external-link"
        iconPosition="right"
        iconVariant="outline"
        iconSize="small"
      >
        {data.key}
      </Link>
    </Flex>
  );
};

export const AssigneeRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  const assignee = data?.assignedTo;
  if (!assignee) {
    return (
      <Flex className={styles.cellContentFlexCenter} alignItems="center">
        <Text variant="regular-sm" color={Colors.Neutral500}>
          Unassigned
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral900}>
        {assignee.displayName}
      </Text>
    </Flex>
  );
};

export const ProgressRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  if (!data?.progress) return null;

  const progress = data.progress;

  if (progress.totalTasks === 0) {
    return (
      <Flex className={styles.cellContentFlexCenter} alignItems="center">
        <Text variant="regular-sm" color={Colors.Neutral700}>
          <span aria-label="No tasks">--</span>
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Flex alignItems="center" gap={8}>
        <div className={styles.progressBarWrapper}>
          <LinearProgressIndicator progressPercentage={progress.percentage} variant="default" />
        </div>
        <Text variant="regular-sm" color={Colors.Neutral700}>
          {progress.completedTasks}/{progress.totalTasks}
        </Text>
      </Flex>
    </Flex>
  );
};

export const ClientRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  const customers = data?.customers;
  const hasCustomers = customers && customers.length > 0;

  if (!hasCustomers) {
    return (
      <Flex className={styles.clientCellContentWithIcon} alignItems="center" gap={8}>
        <Icon type="exclamation-triangle" color={Colors.Danger600} />
        <Text variant="regular-sm" className={styles.dangerText}>
          No assigned client
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral900}>
        {customers.join(", ")}
      </Text>
    </Flex>
  );
};

export const ManagerRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  const manager = data?.manager;
  if (!manager) {
    return (
      <Flex className={styles.cellContentFlexCenter} alignItems="center">
        <Text variant="regular-sm" color={Colors.Neutral500}>
          No Manager
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral900}>
        {manager.displayName}
      </Text>
    </Flex>
  );
};

export const TimeElapsedRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  if (!data || data.timeElapsed <= 0) return null;

  const daysElapsed = data.timeElapsed;

  const formatTimeElapsed = (days: number) => {
    if (days === 1) return "1 day";
    return `${days} days`;
  };

  const getTimeElapsedVariant = (days: number): "danger" | "warning" | "success" => {
    if (days > 10) {
      return "danger";
    } else if (days >= 5) {
      return "warning";
    } else {
      return "success";
    }
  };

  const variant = getTimeElapsedVariant(daysElapsed);

  return (
    <Flex className={styles.cellContentFlexCenter}>
      <Tag variant={variant} label={formatTimeElapsed(daysElapsed)} />
    </Flex>
  );
};

export const StatusRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  value,
}) => {
  if (!value) return null;

  const status = String(value) as TicketStatus;
  const label = formatTicketStatus(status);
  const showIcon = status === "IN_PROGRESS";

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Flex alignItems="center" gap={8}>
        {showIcon && <Icon type="sync-alt" color={Colors.Neutral700} />}
        <Text variant="regular-sm" color={Colors.Neutral900}>
          {label}
        </Text>
      </Flex>
    </Flex>
  );
};

export const PastDueRenderer: React.FC<Partial<TypedAGGridCellRenderParams<TicketRowItem>>> = ({
  data,
}) => {
  if (!data) return null;

  if (data.pastDue === 0) return null;

  const isPastDue = data.pastDue > 0;
  const days = Math.abs(data.pastDue);

  const formatDueStatus = (daysCount: number, isOverdue: boolean) => {
    if (isOverdue) {
      if (daysCount === 1) return "1 day";
      return `${daysCount} days`;
    } else {
      if (daysCount === 1) return "1 day remaining";
      return `${daysCount} days remaining`;
    }
  };

  const variant: "danger" | "success" = isPastDue ? "danger" : "success";

  return (
    <Flex className={styles.cellContentFlexCenter}>
      <Tag variant={variant} label={formatDueStatus(days, isPastDue)} />
    </Flex>
  );
};

export const ManagerOfAssignedToRenderer: React.FC<
  TypedAGGridCellRenderParams<TicketRowItem>
> = ({ data }) => {
  const manager = data?.managerOfAssignedTo;
  if (!manager) return null;

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral900}>
        {manager.displayName}
      </Text>
    </Flex>
  );
};

export const TextCellRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  value,
}) => {
  if (!value) return null;

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral900}>
        {value}
      </Text>
    </Flex>
  );
};

export const EntityRenderer: React.FC<TypedAGGridCellRenderParams<TicketRowItem>> = ({
  data,
}) => {
  const entities = data?.entityName;

  if (!entities || entities.length === 0) {
    return null;
  }

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral900}>
        {entities.join(", ")}
      </Text>
    </Flex>
  );
};
